import React, { useEffect, useRef, useState } from "react";
import {
  Banknote,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Clock,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { paymentSetupQuickGuide } from "../content/salesAffiliatePaymentSetupGuide";

const PAYMENT_FUNCTION = "sales_affiliate_payment_setup";

const PAYMENT_SETUP_ACTIVATION_CLARIFICATION =
  "Payment Setup is not required for Sales Affiliate activation or for signing up Prospects and Customers. However, you must complete Payment Setup before you can receive eligible commission payments.";

const PAYMENT_VALIDATION_CODES = new Set([
  "STRIPE_ONBOARDING_DETAILS_NOT_SUBMITTED",
  "STRIPE_SETUP_VALIDATION_REQUIRED",
]);

function paymentNeedsValidation(requirement) {
  return PAYMENT_VALIDATION_CODES.has(
    String(requirement?.action_required_code || "").toUpperCase()
  );
}

export default function SalesAffiliatePaymentSection({
  requirement,
  refreshWorkspace,
}) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [providerNotice, setProviderNotice] = useState("");
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const returnHandled = useRef(false);
  const statusCheckedOnEntry = useRef(false);

  const presentationState = requirement?.presentation_state || "NOT_CONFIGURED";
  const allowedAction = requirement?.allowed_action || "NOT_CONFIGURED";
  const providerConfigured = Boolean(requirement?.provider_configured);
  const verifiedForPayment = Boolean(requirement?.verified_for_payment);
  const validationRequired = paymentNeedsValidation(requirement);

  const invokePayment = async (action, { redirect = false } = {}) => {
    if (busy) return null;
    setError("");
    setBusy(action);

    try {
      const body = { action };

      const { data, error: fnError } = await supabase.functions.invoke(
        PAYMENT_FUNCTION,
        { body }
      );

      if (fnError) {
        throw new Error(fnError.message || "Unable to update payment setup.");
      }
      if (!data?.ok) {
        throw new Error(data?.error || "Unable to update payment setup.");
      }

      if (redirect && data?.onboarding_url) {
        window.location.assign(data.onboarding_url);
        return data;
      }

      if (action === "status") {
        const actionCode = String(data?.action_required_code || "").toUpperCase();
        if (data?.provider_status === "VERIFIED") {
          setProviderNotice("Stripe has verified your payment setup.");
        } else if (data?.provider_status === "PENDING_VERIFICATION") {
          setProviderNotice(
            "Stripe has received your information and is completing verification."
          );
        } else if (
          data?.provider_status === "ACTION_REQUIRED" &&
          PAYMENT_VALIDATION_CODES.has(actionCode)
        ) {
          setProviderNotice(
            "Your Stripe account is connected, but setup still needs to be validated or completed before payouts."
          );
        } else if (data?.provider_status === "ACTION_REQUIRED") {
          setProviderNotice(
            "Stripe requires additional information. Use Resolve Stripe Requirements to continue."
          );
        }
      }

      await refreshWorkspace({ background: action === "status" });
      return data;
    } catch (err) {
      setError(err?.message || "Unable to update payment setup.");
      return null;
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    if (returnHandled.current || typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const connectAction = url.searchParams.get("stripe_connect");
    if (connectAction !== "return" && connectAction !== "refresh") return;

    returnHandled.current = true;
    url.searchParams.delete("stripe_connect");
    const cleaned = `${url.pathname}${url.search}${url.hash}`;

    if (connectAction === "refresh") {
      window.history.replaceState({}, document.title, cleaned);
      invokePayment("start", { redirect: true });
      return;
    }

    window.history.replaceState({}, document.title, cleaned);
    invokePayment("status");
    // invokePayment and refreshWorkspace are intentionally not dependencies here.
    // This one-time handler is keyed only to Stripe's return/refresh query marker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh Stripe readiness once whenever the Payment Setup section is entered.
  // This prevents a previously stored VERIFIED row from remaining green after
  // Stripe later reports incomplete onboarding or new payout requirements.
  useEffect(() => {
    if (
      statusCheckedOnEntry.current ||
      returnHandled.current ||
      typeof window === "undefined" ||
      !requirement ||
      !providerConfigured ||
      presentationState === "NOT_CONFIGURED"
    ) {
      return;
    }

    const url = new URL(window.location.href);
    const connectAction = url.searchParams.get("stripe_connect");
    if (connectAction === "return" || connectAction === "refresh") return;

    statusCheckedOnEntry.current = true;
    invokePayment("status");
    // invokePayment and refreshWorkspace are intentionally not dependencies here.
    // This is a one-time provider-readiness refresh for this mounted section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerConfigured, presentationState, requirement]);

  if (!requirement) return null;

  const renderProviderNotice = () =>
    providerNotice ? (
      <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800">
        {providerNotice}
      </div>
    ) : null;

  const renderPaymentGuidance = () => (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50">
      <button
        type="button"
        onClick={() => setGuidanceOpen((open) => !open)}
        aria-expanded={guidanceOpen}
        aria-controls="payment-account-provider-guidance"
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left sm:px-5"
      >
        <div>
          <div className="text-sm font-black text-slate-950">
            {paymentSetupQuickGuide.title}
          </div>
          <div className="mt-1 text-xs font-semibold leading-5 text-slate-600">
            Review the information you may need, business/entity guidance, optional Link choice, and what to do if additional verification is required.
          </div>
        </div>
        <ChevronDown
          size={20}
          aria-hidden="true"
          className={`shrink-0 text-slate-500 transition-transform ${
            guidanceOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {guidanceOpen ? (
        <div
          id="payment-account-provider-guidance"
          className="border-t border-slate-200 px-4 py-4 sm:px-5"
        >
          <ul className="space-y-2 pl-5 text-sm leading-6 text-slate-700">
            {paymentSetupQuickGuide.bullets.map((item) => (
              <li key={item} className="list-disc pl-1">
                {item}
              </li>
            ))}
          </ul>

          <Link
            to="/sales-affiliate/payment-setup-guide"
            className="mt-4 inline-flex items-center rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-black text-white hover:bg-blue-800"
          >
            View Step-by-Step Payment Setup Guide
          </Link>
        </div>
      ) : null}
    </div>
  );

  const renderBody = () => {
    if (!providerConfigured || presentationState === "NOT_CONFIGURED") {
      return (
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 flex-none text-amber-600" size={22} />
          <div>
            <div className="text-sm font-black text-slate-900">
              Commission payment setup
            </div>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              The commission payment workflow is not yet available in this
              workspace. No action is required from you until it becomes
              available.
            </p>
            <div className="mt-4 inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-slate-600">
              Not yet available
            </div>
          </div>
        </div>
      );
    }

    if (presentationState === "VERIFIED") {
      return (
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 flex-none text-emerald-600" size={22} />
          <div>
            <div className="text-sm font-black text-slate-900">
              Payment setup verified
            </div>
            <div className="mt-1 text-sm leading-6 text-slate-600">
              Stripe has verified your commission payment setup. Commissions may
              be paid according to the AI Fine Protector payment schedule.
            </div>
            {renderProviderNotice()}
          </div>
        </div>
      );
    }

    if (presentationState === "SUBMITTED_PENDING_VERIFICATION") {
      return (
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 flex-none text-blue-600" size={22} />
          <div>
            <div className="text-sm font-black text-slate-900">
              Awaiting Stripe verification
            </div>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Stripe has received your payment setup information.{" "}
              {PAYMENT_SETUP_ACTIVATION_CLARIFICATION}
            </p>
            {!verifiedForPayment ? (
              <div className="mt-4 inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                Verification pending
              </div>
            ) : null}
            {renderProviderNotice()}
          </div>
        </div>
      );
    }

    const isFailed = presentationState === "FAILED";
    const startLabel = validationRequired
      ? "Validate Stripe Setup"
      : presentationState === "ACTION_REQUIRED"
      ? "Resolve Stripe Requirements"
      : allowedAction === "RESTART_AFTER_FAILURE"
      ? "Restart payment setup"
      : allowedAction === "RESUME_WORKFLOW"
      ? "Continue Stripe Setup"
      : "Start payment setup";

    return (
      <div>
        <div className="text-sm font-black text-slate-900">
          {validationRequired
            ? "Stripe account connected — validation required"
            : "Set up commission payments"}
        </div>
        <p className="mt-1 text-sm leading-6 text-slate-600">
          {validationRequired
            ? "Your Stripe account is connected, but Stripe has not confirmed that all payout-readiness steps are complete. Return to Stripe to finish or validate the remaining setup before commissions can be paid."
            : "Continue to Stripe to securely provide the information required to receive commission payments. Stripe collects and retains your banking and identity-verification information. AI Fine Protector receives only the provider account reference and payment-readiness status needed to administer the Sales Affiliate Program."}
        </p>

        <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">
          {PAYMENT_SETUP_ACTIVATION_CLARIFICATION}
        </p>

        {isFailed ? (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            Stripe reported a problem with your payment setup. Restart the Stripe
            workflow to continue.
          </div>
        ) : null}

        {validationRequired ? (
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-900">
            Use Validate Stripe Setup to return securely to Stripe. Stripe will
            show any remaining steps that apply to your account.
          </div>
        ) : presentationState === "ACTION_REQUIRED" && !isFailed ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
            Stripe requires additional action before your payment setup can be
            verified.
          </div>
        ) : null}

        {renderProviderNotice()}

        {error ? (
          <div
            role="alert"
            className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
          >
            {error}
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={Boolean(busy)}
            onClick={() => invokePayment("start", { redirect: true })}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy === "start" ? "Opening Stripe..." : startLabel}
            {busy !== "start" ? <ExternalLink size={16} /> : null}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section
      id="payment"
      className="mt-8 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm"
    >
      <div className="flex items-center gap-3">
        <Banknote className="text-blue-600" size={24} />
        <h2 className="text-xl font-black text-slate-950">
          Commission Payment Setup
        </h2>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5">
        {renderBody()}
      </div>

      {providerConfigured ? renderPaymentGuidance() : null}
    </section>
  );
}
